// src/converters/stage.ts
import { Stage, Season, Championship, StageTeam, Team } from '@/model';
import {
  StageItemResponse,
  StageSeasonResponse,
  StageChampionshipResponse,
  StageTeamSummaryResponse,
  StageTeamItemResponse,
  GetStageResponse,
  GetStageByIdResponse,
  PostStagesBatchResponse,
  PutStageResponse,
  PutStageCurrentRoundResponse,
  PutStageMainWidgetResponse,
  GetStageTeamsResponse,
  PostStageTeamResponse,
  PostStageBatchTeamsResponse,
} from '@/services/response/stage';

const mapChampionship = (
  response?: StageChampionshipResponse,
): Championship | undefined => {
  if (!response) return undefined;

  return {
    uuid: response.uuid,
    name: response.name,
    is_filtered: response.is_filtered ?? false,
    is_active: response.is_active ?? true,
    created_at: '',
    updated_at: '',
  };
};

const mapSeason = (response?: StageSeasonResponse): Season | undefined => {
  if (!response) return undefined;

  return {
    uuid: response.uuid,
    championship_uuid: response.championship?.uuid ?? '',
    type: response.type,
    year: response.year,
    is_filtered: response.is_filtered ?? false,
    is_active: response.is_active ?? true,
    championship: mapChampionship(response.championship),
    created_at: '',
    updated_at: '',
  };
};

const mapTeamSummary = (response: StageTeamSummaryResponse): Team => ({
  uuid: response.uuid,
  name: response.name,
  acronym: response.acronym ?? '',
  logo_path: response.logo_path,
  aliases: undefined,
  x_profile_id: undefined,
  favorite: undefined,
  selectable_in_menu: undefined,
  deleted_at: undefined,
} as unknown as Team);

export class StageConverter {
  static toDomainFromItemResponse(response: StageItemResponse): Stage {
    return {
      uuid: response.uuid,
      season_uuid: response.season?.uuid,
      season: mapSeason(response.season),
      is_main_widget: response.is_main_widget,
      name: response.name,
      type: response.type,
      current_round: response.current_round ?? undefined,
      stage_order: response.stage_order,
      group_name: response.group_name ?? undefined,
      direct_qualifiers_count: response.direct_qualifiers_count ?? 0,
      playoff_qualifiers_count: response.playoff_qualifiers_count ?? 0,
      secondary_qualifiers_count: response.secondary_qualifiers_count ?? 0,
      relegated_count: response.relegated_count ?? 0,
      direct_qualifiers_label: response.direct_qualifiers_label ?? null,
      playoff_qualifiers_label: response.playoff_qualifiers_label ?? null,
      secondary_qualifiers_label: response.secondary_qualifiers_label ?? null,
      relegated_qualifiers_label: response.relegated_qualifiers_label ?? null,
      created_at: response.created_at,
      updated_at: response.updated_at,
    };
  }

  static toDomainFromPaginatedResponse(response: GetStageResponse): Stage[] {
    return response.data.map(StageConverter.toDomainFromItemResponse);
  }

  static toDomainFromByIdResponse(response: GetStageByIdResponse): Stage {
    return StageConverter.toDomainFromItemResponse(response);
  }

  static toDomainFromPostBatchResponse(response: PostStagesBatchResponse): Stage[] {
    return response.data.map(StageConverter.toDomainFromItemResponse);
  }

  static toDomainFromPutResponse(response: PutStageResponse): Stage {
    return StageConverter.toDomainFromItemResponse(response);
  }

  static toDomainFromPutCurrentRoundResponse(
    response: PutStageCurrentRoundResponse,
  ): Stage {
    return StageConverter.toDomainFromItemResponse(response);
  }

  static toDomainFromPutMainWidgetResponse(
    response: PutStageMainWidgetResponse,
  ): Stage {
    return StageConverter.toDomainFromItemResponse(response);
  }

  static toDomainFromStageTeamItem(response: StageTeamItemResponse): StageTeam {
    return {
      uuid: response.uuid,
      stage_uuid: response.stage?.uuid,
      team_uuid: response.team.uuid,
      team: mapTeamSummary(response.team),
      created_at: response.created_at,
      updated_at: response.updated_at,
    };
  }

  static toDomainFromStageTeamsResponse(
    response: GetStageTeamsResponse,
  ): StageTeam[] {
    return response.data.map(StageConverter.toDomainFromStageTeamItem);
  }

  static toDomainFromPostStageTeamResponse(
    response: PostStageTeamResponse,
  ): StageTeam {
    return StageConverter.toDomainFromStageTeamItem(response);
  }

  static toDomainFromPostStageBatchTeamsResponse(
    response: PostStageBatchTeamsResponse,
  ): StageTeam[] {
    return response.data.map(StageConverter.toDomainFromStageTeamItem);
  }
}
